import { EventEmitter } from '@stencil/core';
import { MFEEligibilityEventDetail } from '@vanguard/mfe-events';
import { PlanDetails, Plan, PayrollDeferralsCurrent } from '@vanguard/piig-xover-widgets-types';

export interface TimestampableData {
  timestamp?: number | string | Date;
  pdSourceSuspendedValueList?: Array<{ processDateTime?: string; processDateTimeing?: string }>;
  pdSourceEligibleNotSuspendedValueList?: Array<{ processDateTime?: string; processDateTimeing?: string }>;
  pdSourceIneligibleValueList?: Array<{ processDateTime?: string; processDateTimeing?: string }>;
  [key: string]: unknown;
}

export const getFormattedNumber = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '';
  }
  const formattedAmount = Math.abs(amount).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `\u2212${formattedAmount}` : formattedAmount;
};

/**
 * Formats a number as currency using toLocaleString with configurable decimal places
 * @param amount - The number to format
 * @param decimalPlaces - Number of decimal places to show (default: 2)
 * @returns Formatted currency string without currency symbol
 */
export const formatCurrencyAmount = (amount: number, decimalPlaces: number = 2): string => {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

//function to check if the last two digits of a string(poid) are between 00 and 25
export const checkNumberString = (numStr: string): boolean => {
  // Extract the last two digits from the string
  const lastTwoDigits = parseInt(numStr.slice(-2), 10);

  // Check if the last two digits are between 05 and 30
  return lastTwoDigits >= 5 && lastTwoDigits <= 30;
};

//check eligibility for the poid
export const checkPoidEligibility = (): boolean => {
  const metaTag = document.querySelector('meta[name="DCSext.poid"]');
  const poid = metaTag ? metaTag.getAttribute('content') : null;
  // test case: poid between 00 and 25
  // poid = '29647125'
  // test case: could not get poid
  // poid = null;
  // Check if the poid is a string

  if (typeof poid === 'string') {
    // Check if the last two digits of the poid are between 00 and 25
    return checkNumberString(poid);
  } else {
    return true;
  }
};

export function splitObject(obj: Record<string, unknown>, ...propertyGroups: string[][]): Record<string, unknown>[] {
  return propertyGroups.map((group) => {
    const o: Record<string, unknown> = {};
    for (const key of group) {
      o[key] = obj[key];
    }
    return o;
  });
}

export const terminatedStatusCodes = ['30', '31', '32', '33', '34', '35'];

interface PlanWithStatusCodes {
  partStatusCode?: string;
  prsStatusCode?: string;
}

export const countTerminatedPlans = (plans: PlanWithStatusCodes[]): number => {
  let terminatedCount = 0;

  plans.forEach((plan) => {
    if (plan.partStatusCode && terminatedStatusCodes.includes(plan.partStatusCode)) {
      terminatedCount += 1;
    }
  });

  return terminatedCount;
};

export const countActivePlans = (plans: PlanWithStatusCodes[]): number => {
  let activeCount = 0;

  plans.forEach((plan) => {
    if (plan.prsStatusCode === '00' && plan.partStatusCode === '00') {
      activeCount++;
    }
  });

  return activeCount;
};

export function someValuesMatch<T>(values: T[], matchValue: T): boolean {
  return values.some((value) => value === matchValue);
}

export function extractValuesByKey<T>(objects: T[], matchKey: keyof T): T[keyof T][] {
  return objects.map((obj) => obj[matchKey]);
}

/**
 * This function will check if the user updated within 60 days
 * @param data array
 * @returns boolean
 */
export function checkTimestamp(data: TimestampableData | PayrollDeferralsCurrent | null | undefined): boolean {
  if (!data) {
    return false;
  }

  const { pdSourceSuspendedValueList = [], pdSourceEligibleNotSuspendedValueList = [], pdSourceIneligibleValueList = [] } = data;

  // Combine all lists into one
  const allLists = [...pdSourceSuspendedValueList, ...pdSourceEligibleNotSuspendedValueList, ...pdSourceIneligibleValueList];

  // Extract all timestamps (handle both processDateTime and processDateTimeing for backward compatibility)
  const timestamps = allLists
    .map((item) => {
      // Access as any to handle both TimestampableData (with processDateTimeing) and PayrollDeferralsCurrent (without)
      const anyItem = item as { processDateTime?: string; processDateTimeing?: string };
      return anyItem.processDateTime || anyItem.processDateTimeing;
    })
    .filter((timestamp) => timestamp !== undefined && timestamp.trim() !== '')
    .map((timestamp) => {
      // Trim after the year-month-date part (first 10 characters)
      // const trimmedTimestamp = timestamp!.substring(0, 10);
      return new Date(timestamp!);
    });

  if (timestamps.length === 0) {
    return false; // No timestamps found
  }

  // Find the latest timestamp
  const latestTimestamp = new Date(Math.max(...timestamps.map((date) => date.getTime())));
  // Calculate the difference in days
  const currentTime = new Date();
  const diffInDays = (currentTime.getTime() - latestTimestamp.getTime()) / (1000 * 60 * 60 * 24);
  return diffInDays < 60;
}

interface ObjectWithPathAttribute {
  _path?: string;
  [key: string]: unknown;
}

export const transformArrayToKeyValueByPath = (
  inputArray: Array<ObjectWithPathAttribute>,
  numberOfPathfragmentsAsKey: number = 2,
  pathConcatCharacter: string = '_'
): Record<string, ObjectWithPathAttribute> => {
  const transformedKeyValuePair: Record<string, ObjectWithPathAttribute> = {};

  inputArray.reduce((acc: Record<string, ObjectWithPathAttribute>, current: ObjectWithPathAttribute) => {
    if (current._path != null && current._path !== '') {
      const splitPath = current._path.split('/');
      if (numberOfPathfragmentsAsKey > splitPath.length) {
        throw new Error('The numberOfPathfragmentsAsKey is greater than the number of path fragments in the path');
      }
      // Generate the key and replace hyphens with underscores
      const key = splitPath
        .slice(splitPath.length - numberOfPathfragmentsAsKey, splitPath.length)
        .join(pathConcatCharacter)
        .replace(/-/g, '_'); // Replace hyphens with underscores
      acc[key] = current;
    }
    return acc;
  }, transformedKeyValuePair);

  return transformedKeyValuePair;
};

export const getHighestBalancePlan = (plansDetail: PlanDetails[], plans: Plan[]): string => {
  if (plansDetail.length === 0 || plans.length === 0) {
    throw new Error('The plans or plansDetail array is empty.');
  }

  // Filter the plansDetail array to include only items with planId present in the plans array
  const filteredPlansDetail = plansDetail.filter((planDetail) => plans.some((plan) => plan.planID === planDetail.planId));

  if (filteredPlansDetail.length === 0) {
    throw new Error('No matching plans found after filtering.');
  }

  const highestBalancePlan = filteredPlansDetail.reduce((maxPlan, currentPlan) =>
    currentPlan.balance > maxPlan.balance ? currentPlan : maxPlan
  );

  return highestBalancePlan.planId;
};

export const formatDate = (dateString: string | null | undefined): string => {
  if (dateString == null || dateString.trim() === '') {
    return '';
  }
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

export const adobeInternalBannerImpression = (tagName: string): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'internalBannerImpression',
    internalBannerImpression: `${tagName} viewport`, //v206
  });
};

/**
 * Shared function to dispatch eligibility event across components
 * @param eventEmitter - The EventEmitter instance from the component
 * @param tagName - The tag name of the component
 * @param eligible - Whether the component is eligible or not
 * @param callback - Optional callback function to execute after dispatching the event
 */
export const dispatchEligibilityEvent = (
  eventEmitter: EventEmitter<MFEEligibilityEventDetail>,
  tagName: string,
  eligible: boolean = false,
  callback?: () => void
): void => {
  eventEmitter.emit({
    tagName,
    eligible,
  });

  if (eligible === true) {
    adobeInternalBannerImpression(tagName);
  }

  callback?.();
};
